/**
 * IndustryChip — small Mission Control header chip showing which
 * industry profile is currently in play for the effective org.
 *
 * Hover (or focus) opens a richer audit pane with the top KPI
 * priorities, dampening, max-uplift cap, and which org-level overrides
 * are layered on top. Click jumps to the Industry sub-tab.
 *
 * Hidden when:
 *  - no org context
 *  - the org has no industry tagged (fall-back to global defaults)
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, ExternalLink, Pin } from 'lucide-react';
import {
  HoverCard, HoverCardTrigger, HoverCardContent,
} from '../ui/hover-card';
import useIndustryProfile from '../../hooks/useIndustryProfile';

function _humanise(id) {
  return String(id || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function _formatStamp(iso) {
  if (!iso) return 'never';
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return 'unknown';
    const diff = Date.now() - t;
    const m = Math.round(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (e) {
    return 'unknown';
  }
}

export default function IndustryChip({ orgId }) {
  const navigate = useNavigate();
  const { profile, loading } = useIndustryProfile(orgId);

  if (loading || !profile?.industry_label) return null;

  const handleClick = () => {
    if (!orgId) return;
    navigate('/admin');
  };

  // Source-map analytics — count cells overridden at the org level so
  // the audit pane can say "+ 3 org overrides on top".
  const sm = profile.source_map || {};
  const orgOverrideCount =
    Object.entries(sm).reduce((acc, [, val]) => {
      if (val === 'org_override') return acc + 1;
      if (typeof val === 'object' && val !== null) {
        // nested (kpi_baselines, lei_kpi_weights, elasticities)
        return acc + Object.values(val).reduce((a, v) => {
          if (v === 'org_override') return a + 1;
          if (typeof v === 'object' && v !== null) {
            return a + Object.values(v).filter((x) => x === 'org_override').length;
          }
          return a;
        }, 0);
      }
      return acc;
    }, 0);

  const topKpis = (profile.kpis || []).slice(0, 3);

  return (
    <HoverCard openDelay={150} closeDelay={120}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          data-testid="header-industry-chip"
          data-industry-id={profile.industry_id}
          aria-label={`ROI model: ${profile.industry_label}. Hover for details, click to manage.`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '4px 9px 4px 8px',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.3,
            color: 'rgb(67, 56, 202)',
            background: 'rgb(238, 242, 255)',
            border: '1px solid rgb(199, 210, 254)',
            borderRadius: 999,
            maxWidth: 240,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            transition: 'background-color 150ms, border-color 150ms',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgb(224, 231, 255)';
            e.currentTarget.style.borderColor = 'rgb(165, 180, 252)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgb(238, 242, 255)';
            e.currentTarget.style.borderColor = 'rgb(199, 210, 254)';
          }}
        >
          <Layers size={11} aria-hidden />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {profile.industry_label}
          </span>
          {orgOverrideCount > 0 && (
            <span
              data-testid="header-industry-chip-override-badge"
              title={`${orgOverrideCount} org-level override(s) on top of the industry default`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                marginLeft: 2,
                padding: '0 5px',
                fontSize: 9,
                fontWeight: 700,
                color: 'rgb(180, 83, 9)',
                background: 'rgb(254, 243, 199)',
                border: '1px solid rgb(252, 211, 77)',
                borderRadius: 999,
              }}
            >
              <Pin size={8} aria-hidden /> {orgOverrideCount}
            </span>
          )}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="end"
        className="w-80 p-0 shadow-xl border-indigo-100"
        data-testid="header-industry-chip-popover"
      >
        <div className="px-4 py-3 border-b bg-gradient-to-r from-indigo-50 to-white">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-indigo-600">
            ROI Model · resolved
          </div>
          <div className="text-base font-bold text-slate-900 flex items-center gap-1.5 mt-0.5">
            <Layers className="h-4 w-4 text-indigo-600" />
            {profile.industry_label}
          </div>
          {orgOverrideCount > 0 && (
            <div className="text-[11px] text-amber-700 mt-1">
              + <strong>{orgOverrideCount}</strong> org-level override{orgOverrideCount === 1 ? '' : 's'} on top
            </div>
          )}
        </div>

        <div className="px-4 py-3 space-y-3">
          {/* Top 3 KPIs */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
              Headline KPIs
            </div>
            <div className="mt-1 space-y-1">
              {topKpis.map((k, idx) => (
                <div
                  key={k.id}
                  className="flex items-center justify-between gap-2 text-xs"
                  data-testid={`header-industry-chip-kpi-${idx}`}
                >
                  <span className="flex items-center gap-1.5 text-slate-700">
                    <span className="text-[10px] text-indigo-400 font-mono">#{idx + 1}</span>
                    {_humanise(k.id)}
                  </span>
                  <span className="font-mono font-semibold text-slate-800">
                    ${Math.round(k.baseline_per_employee || 0).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Caps */}
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
              <div className="text-[9px] uppercase tracking-wider text-slate-500">Dampening</div>
              <div className="font-mono font-semibold text-slate-800">
                {Number(profile.portfolio_dampening_factor ?? 0).toFixed(2)}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
              <div className="text-[9px] uppercase tracking-wider text-slate-500">Max KPI uplift</div>
              <div className="font-mono font-semibold text-slate-800">
                {Number(profile.max_kpi_uplift_percent ?? 0).toFixed(1)}%
              </div>
            </div>
          </div>

          {/* LLM context preview (clipped) */}
          {profile.llm_industry_context && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                AI tone
              </div>
              <p className="text-[11px] text-slate-600 italic mt-0.5 leading-relaxed line-clamp-3">
                “{profile.llm_industry_context}”
              </p>
            </div>
          )}
        </div>

        <div className="px-4 py-2 border-t bg-slate-50 flex items-center justify-between">
          <div className="text-[10px] text-slate-500">
            Synced {_formatStamp(profile?.updated_at)}
          </div>
          <button
            type="button"
            onClick={handleClick}
            data-testid="header-industry-chip-manage"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900"
          >
            Manage <ExternalLink size={10} />
          </button>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
